/**
 * Attribute type validation schemas.
 * These schemas validate actual attribute VALUES against their type definitions.
 * Used for validating product attributes and user inputs at runtime.
 */
import { z } from "zod";
// ============================================================================
// Common Patterns
// ============================================================================
/** ISO 8601 date pattern (date only) */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/** ISO 8601 datetime pattern */
const ISO_DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;
/** Email pattern (RFC 5322 simplified) */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** URL pattern */
const URL_PATTERN = /^https?:\/\/[^\s]+$/;
/** Phone pattern (international format) */
const PHONE_PATTERN = /^\+?[\d\s\-()]+$/;
// ============================================================================
// Base Value Schemas (without constraints)
// ============================================================================
/** String value schema */
export const StringValueSchema = z.string();
/** Number value schema */
export const NumberValueSchema = z.number().finite();
/** Boolean value schema */
export const BooleanValueSchema = z.boolean();
/** Enum value schema (base - values validated separately) */
export const EnumValueSchema = z.string().min(1);
/** Date value schema (ISO 8601) */
export const DateValueSchema = z
    .string()
    .refine((val) => ISO_DATE_PATTERN.test(val) || ISO_DATETIME_PATTERN.test(val), {
    message: "Date must be in ISO 8601 format (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ssZ)",
});
/** Currency value schema (number representing amount) */
export const CurrencyValueSchema = z.number().finite();
/** Percentage value schema */
export const PercentageValueSchema = z.number().finite();
/** URL value schema */
export const UrlValueSchema = z
    .string()
    .refine((val) => URL_PATTERN.test(val), { message: "Value must be a valid URL" });
/** Email value schema */
export const EmailValueSchema = z
    .string()
    .refine((val) => EMAIL_PATTERN.test(val), { message: "Value must be a valid email address" });
/** Phone value schema */
export const PhoneValueSchema = z
    .string()
    .refine((val) => PHONE_PATTERN.test(val), { message: "Value must be a valid phone number" });
// ============================================================================
// Constrained Value Schema Builders
// ============================================================================
/**
 * Creates a string schema with constraints applied.
 */
export function createStringSchema(constraints) {
    let schema = z.string();
    if (constraints) {
        if (constraints.minLength !== undefined) {
            schema = schema.min(constraints.minLength);
        }
        if (constraints.maxLength !== undefined) {
            schema = schema.max(constraints.maxLength);
        }
        if (constraints.pattern !== undefined) {
            schema = schema.regex(new RegExp(constraints.pattern), {
                message: `Value must match pattern: ${constraints.pattern}`,
            });
        }
    }
    return schema;
}
/**
 * Creates a number schema with constraints applied.
 */
export function createNumberSchema(constraints) {
    let schema = z.number().finite();
    if (constraints) {
        if (constraints.min !== undefined) {
            schema = schema.min(constraints.min);
        }
        if (constraints.max !== undefined) {
            schema = schema.max(constraints.max);
        }
        if (constraints.integer === true) {
            schema = schema.int();
        }
        if (constraints.step !== undefined) {
            schema = schema.multipleOf(constraints.step);
        }
    }
    return schema;
}
/**
 * Creates a boolean schema.
 */
export function createBooleanSchema() {
    return z.boolean();
}
/**
 * Creates an enum schema with allowed values.
 */
export function createEnumSchema(constraints) {
    const allowedValues = constraints.options.map((opt) => opt.value);
    return z.string().refine((val) => allowedValues.includes(val), {
        message: `Value must be one of: ${allowedValues.join(", ")}`,
    });
}
/**
 * Creates a date schema with constraints applied.
 */
export function createDateSchema(constraints) {
    const baseSchema = z.string();
    return baseSchema.refine((val) => {
        // Check format
        const format = constraints?.format ?? "datetime";
        if (format === "date") {
            if (!ISO_DATE_PATTERN.test(val)) {
                return false;
            }
        }
        else if (format === "time") {
            // Time format: HH:mm:ss
            if (!/^\d{2}:\d{2}:\d{2}$/.test(val)) {
                return false;
            }
            return true;
        }
        else {
            // datetime
            if (!ISO_DATETIME_PATTERN.test(val) && !ISO_DATE_PATTERN.test(val)) {
                return false;
            }
        }
        // Parse and check constraints
        const dateValue = new Date(val);
        if (isNaN(dateValue.getTime())) {
            return false;
        }
        if (constraints?.minDate !== undefined) {
            const minDate = new Date(constraints.minDate);
            if (dateValue < minDate) {
                return false;
            }
        }
        if (constraints?.maxDate !== undefined) {
            const maxDate = new Date(constraints.maxDate);
            if (dateValue > maxDate) {
                return false;
            }
        }
        return true;
    }, {
        message: "Invalid date value or date is out of range",
    });
}
/**
 * Creates a currency schema with constraints applied.
 */
export function createCurrencySchema(constraints) {
    let schema = z.number().finite();
    if (constraints.min !== undefined) {
        schema = schema.min(constraints.min);
    }
    if (constraints.max !== undefined) {
        schema = schema.max(constraints.max);
    }
    // If precision is specified, validate that the number doesn't have more decimal places
    if (constraints.precision !== undefined) {
        const precision = constraints.precision;
        return schema.refine((val) => {
            const decimalPart = val.toString().split(".")[1];
            if (!decimalPart)
                return true;
            return decimalPart.length <= precision;
        }, {
            message: `Value must have at most ${constraints.precision} decimal places`,
        });
    }
    return schema.refine(() => true, {});
}
/**
 * Creates a percentage schema with constraints applied.
 * Percentage base can be 100 (0-100) or 1 (0-1).
 */
export function createPercentageSchema(constraints, base = 100) {
    let schema = z.number().finite();
    const effectiveMin = constraints?.min ?? 0;
    const effectiveMax = constraints?.max ?? (base === 100 ? 100 : 1);
    schema = schema.min(effectiveMin);
    schema = schema.max(effectiveMax);
    // If precision is specified, validate decimal places
    if (constraints?.precision !== undefined) {
        const precision = constraints.precision;
        return schema.refine((val) => {
            const decimalPart = val.toString().split(".")[1];
            if (!decimalPart)
                return true;
            return decimalPart.length <= precision;
        }, {
            message: `Value must have at most ${constraints.precision} decimal places`,
        });
    }
    return schema.refine(() => true, {});
}
/**
 * Creates an array schema with constraints applied.
 */
export function createArraySchema(itemSchema, constraints) {
    let schema = z.array(itemSchema);
    if (constraints) {
        if (constraints.minItems !== undefined) {
            schema = schema.min(constraints.minItems);
        }
        if (constraints.maxItems !== undefined) {
            schema = schema.max(constraints.maxItems);
        }
    }
    return schema;
}
// ============================================================================
// Dynamic Schema Builder
// ============================================================================
/**
 * Creates a Zod schema for validating an attribute value based on its definition.
 * This is the main entry point for dynamic attribute validation.
 */
export function createAttributeValueSchema(definition) {
    const { type, constraints, required } = definition;
    let schema;
    switch (type) {
        case "string":
            schema = createStringSchema(constraints);
            break;
        case "number":
            schema = createNumberSchema(constraints);
            break;
        case "boolean":
            schema = createBooleanSchema();
            break;
        case "enum": {
            if (!constraints || !("options" in constraints)) {
                throw new Error(`Enum attribute "${definition.key}" must have options constraint`);
            }
            schema = createEnumSchema(constraints);
            break;
        }
        case "date":
            schema = createDateSchema(constraints);
            break;
        case "currency": {
            if (!constraints || !("currency" in constraints)) {
                throw new Error(`Currency attribute "${definition.key}" must have currency constraint`);
            }
            schema = createCurrencySchema(constraints);
            break;
        }
        case "percentage":
            schema = createPercentageSchema(constraints);
            break;
        case "url":
            schema = UrlValueSchema;
            break;
        case "email":
            schema = EmailValueSchema;
            break;
        case "phone":
            schema = PhoneValueSchema;
            break;
        case "array": {
            const arrayConstraints = constraints;
            const itemType = arrayConstraints?.itemType ?? "string";
            // Create item schema based on item type
            let itemSchema;
            switch (itemType) {
                case "string":
                    itemSchema = z.string();
                    break;
                case "number":
                    itemSchema = z.number();
                    break;
                case "boolean":
                    itemSchema = z.boolean();
                    break;
                case "enum":
                    // For array of enums, we need nested constraints
                    itemSchema = z.string();
                    break;
                case "date":
                    itemSchema = DateValueSchema;
                    break;
                case "currency":
                    itemSchema = z.number();
                    break;
                case "percentage":
                    itemSchema = z.number().min(0).max(100);
                    break;
                case "url":
                    itemSchema = UrlValueSchema;
                    break;
                case "email":
                    itemSchema = EmailValueSchema;
                    break;
                case "phone":
                    itemSchema = PhoneValueSchema;
                    break;
                default:
                    itemSchema = z.unknown();
            }
            schema = createArraySchema(itemSchema, arrayConstraints);
            break;
        }
        default:
            schema = z.unknown();
    }
    // Handle optional fields
    if (!required) {
        schema = schema.optional().nullable();
    }
    return schema;
}
/**
 * Creates a Zod object schema from an array of attribute definitions.
 * Used to validate complete product attribute objects or user input objects.
 */
export function createAttributeObjectSchema(definitions) {
    const shape = {};
    for (const def of definitions) {
        shape[def.key] = createAttributeValueSchema(def);
    }
    return z.object(shape).strict();
}
